/**
 * オフライン布シミュレーション。
 * fill / path ブレースから格子を作り、XPBD で一度だけ焼いて ClothBake にする。
 * volume は閉じた角柱または梁、sheet は塗った多角形や帯の一枚。毎フレームは回さない。
 * 再実行は常に元の brace から始める。
 */

import {
  asPlaneGeometry,
  braceExtrudeOffset,
  braceSectionRings,
  isFillGeometry,
  planeBasis,
  tessellatePlanePolygon,
  tessellateSlab,
  vec,
  type TessellatedSlab,
} from './geometry';
import { ensureWinding, pointInPolygon, triangulatePolygon, triangulatePolygonWithHoles, type Point2D } from './holes';
import type { BraceGeometry, ClothBake, ClothMesh, ClothPin, ClothSimSettings, Vec3 } from './types';
import { computePolygonNormal } from './validation';
import { gridCoords, gridLevels, type GridAxis } from './view-grid';

export const MIN_CLOTH_RESOLUTION = 4;
export const MAX_CLOTH_RESOLUTION = 100;
export const MIN_CLOTH_STEPS = 20;
export const MAX_CLOTH_STEPS = 400;
export const MAX_CLOTH_INFLATE = 8;
export const MAX_CLOTH_GRAVITY = 4;
export const MAX_CLOTH_VERTICES = 32768;
export const MAX_CLOTH_INDICES = 196608;
const DT = 1 / 60;
const DAMPING = 0.985;
const PRESSURE_SCALE = 0.22;
const XPBD_ITERS = 8;
const CHUNK_STEPS = 8;

export const DEFAULT_CLOTH_SETTINGS: ClothSimSettings = {
  resolution: 8,
  steps: 160,
  inflate: 0.35,
  gravity: 0.8,
  stiffness: 0.85,
  shape: 'volume',
  pin: 'none',
  gather: 0,
};

const CLOTH_SHAPES = ['volume', 'sheet'] as const;
const CLOTH_PINS = ['none', 'top', 'bottom', 'left', 'right', 'ends'] as const;

export function hasClothBake(geometry: BraceGeometry): boolean {
  return geometry.cloth !== undefined && geometry.cloth.mesh.positions.length >= 9;
}

export function clampClothSettings(input: Partial<ClothSimSettings> | undefined): ClothSimSettings {
  const src = input ?? {};
  return {
    resolution: clampInt(src.resolution, DEFAULT_CLOTH_SETTINGS.resolution, MIN_CLOTH_RESOLUTION, MAX_CLOTH_RESOLUTION),
    steps: clampInt(src.steps, DEFAULT_CLOTH_SETTINGS.steps, MIN_CLOTH_STEPS, MAX_CLOTH_STEPS),
    inflate: clampNum(src.inflate, DEFAULT_CLOTH_SETTINGS.inflate, 0, MAX_CLOTH_INFLATE),
    gravity: clampNum(src.gravity, DEFAULT_CLOTH_SETTINGS.gravity, 0, MAX_CLOTH_GRAVITY),
    stiffness: clampNum(src.stiffness, DEFAULT_CLOTH_SETTINGS.stiffness, 0, 1),
    shape: pickEnum(src.shape, CLOTH_SHAPES, DEFAULT_CLOTH_SETTINGS.shape),
    pin: pickEnum(src.pin, CLOTH_PINS, DEFAULT_CLOTH_SETTINGS.pin),
    gather: clampNum(src.gather, DEFAULT_CLOTH_SETTINGS.gather, 0, 1),
  };
}

export function clothMeshToMeshData(mesh: ClothMesh): {
  positions: Float32Array;
  normals: Float32Array;
  uvs: Float32Array;
  indices: Uint32Array;
} {
  return {
    positions: new Float32Array(mesh.positions),
    normals: new Float32Array(mesh.normals),
    uvs: new Float32Array(mesh.uvs),
    indices: new Uint32Array(mesh.indices),
  };
}

export function clothBakeAabb(cloth: ClothBake): { min: Vec3; max: Vec3 } | null {
  const p = cloth.mesh.positions;
  if (p.length < 3) return null;
  let minX = Infinity;
  let minY = Infinity;
  let minZ = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let maxZ = -Infinity;
  for (let i = 0; i < p.length; i += 3) {
    const x = p[i];
    const y = p[i + 1];
    const z = p[i + 2];
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (z < minZ) minZ = z;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
    if (z > maxZ) maxZ = z;
  }
  return { min: { x: minX, y: minY, z: minZ }, max: { x: maxX, y: maxY, z: maxZ } };
}

export function mapClothBake(
  cloth: ClothBake,
  mapPoint: (point: Vec3) => Vec3,
  mapVec: (value: Vec3) => Vec3
): ClothBake {
  const positions = cloth.mesh.positions;
  const normals = cloth.mesh.normals;
  const nextPositions = new Array<number>(positions.length);
  const nextNormals = new Array<number>(normals.length);
  for (let i = 0; i < positions.length; i += 3) {
    const p = mapPoint({ x: positions[i], y: positions[i + 1], z: positions[i + 2] });
    nextPositions[i] = p.x;
    nextPositions[i + 1] = p.y;
    nextPositions[i + 2] = p.z;
    const n = mapVec({ x: normals[i], y: normals[i + 1], z: normals[i + 2] });
    const len = Math.hypot(n.x, n.y, n.z);
    if (len < 1e-9) {
      nextNormals[i] = n.x;
      nextNormals[i + 1] = n.y;
      nextNormals[i + 2] = n.z;
    } else {
      nextNormals[i] = n.x / len;
      nextNormals[i + 1] = n.y / len;
      nextNormals[i + 2] = n.z / len;
    }
  }
  return {
    settings: { ...cloth.settings },
    mesh: {
      positions: nextPositions,
      normals: nextNormals,
      uvs: cloth.mesh.uvs.slice(),
      indices: cloth.mesh.indices.slice(),
    },
  };
}

export function translateClothBake(cloth: ClothBake, delta: Vec3): ClothBake {
  return mapClothBake(
    cloth,
    (point) => ({ x: point.x + delta.x, y: point.y + delta.y, z: point.z + delta.z }),
    (value) => value
  );
}

export function canSimulateCushion(geometry: BraceGeometry): boolean {
  if (isFillGeometry(geometry)) {
    return (
      buildCushionRest(geometry, DEFAULT_CLOTH_SETTINGS.resolution) !== null ||
      buildSheetRest(geometry, DEFAULT_CLOTH_SETTINGS.resolution) !== null
    );
  }
  return buildPathRest(geometry, DEFAULT_CLOTH_SETTINGS) !== null;
}

/** path は両端固定・ふくらみなしを初期値にする（電線）。 */
export function defaultClothSettingsFor(geometry: BraceGeometry): ClothSimSettings {
  if (isFillGeometry(geometry)) return { ...DEFAULT_CLOTH_SETTINGS };
  return { ...DEFAULT_CLOTH_SETTINGS, pin: 'ends', inflate: 0 };
}

/** テストと同期実行用。UI は simulateCushionClothAsync を使う。 */
export function simulateCushionCloth(
  geometry: BraceGeometry,
  settings: Partial<ClothSimSettings> = {}
): ClothBake | null {
  const clamped = clampClothSettings(settings);
  const rest = buildClothRest(geometry, clamped);
  if (!rest) return null;
  const state = createSimState(rest, clamped);
  for (let step = 0; step < clamped.steps; step++) stepSim(state);
  return bakeState(state, clamped);
}

export async function simulateCushionClothAsync(
  geometry: BraceGeometry,
  settings: Partial<ClothSimSettings>,
  onProgress?: (progress: number) => void,
  signal?: AbortSignal,
  onFrame?: (bake: ClothBake) => void
): Promise<ClothBake | null> {
  const clamped = clampClothSettings(settings);
  const rest = buildClothRest(geometry, clamped);
  if (!rest) return null;
  const state = createSimState(rest, clamped);
  const total = Math.max(1, clamped.steps);
  let last = bakeState(state, clamped);
  onProgress?.(0);
  onFrame?.(last);
  for (let step = 0; step < total; ) {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    const until = Math.min(total, step + CHUNK_STEPS);
    for (; step < until; step++) stepSim(state);
    last = bakeState(state, clamped);
    onProgress?.(step / total);
    onFrame?.(last);
    if (step < total) await yieldFrame();
  }
  onProgress?.(1);
  return last;
}

interface RestFace {
  indices: number[];
  uv: Map<number, [number, number]>;
}

interface RestMesh {
  positions: number[];
  indices: number[];
  faces: RestFace[];
  /** 塗った面の法線。固定辺の上下左右を決める。 */
  normal: Vec3;
  /** path の中心線。両端固定の距離判定に使う。 */
  pathLine?: { points: Vec3[]; closed: boolean };
}

interface DistConstraint {
  i: number;
  j: number;
  rest: number;
}

interface SimState {
  x: Float64Array;
  y: Float64Array;
  z: Float64Array;
  px: Float64Array;
  py: Float64Array;
  pz: Float64Array;
  pinX: Float64Array;
  pinY: Float64Array;
  pinZ: Float64Array;
  pinned: Uint8Array;
  constraints: DistConstraint[];
  indices: number[];
  faces: RestFace[];
  inflate: number;
  gravity: number;
  compliance: number;
  floorY: number;
  useFloor: boolean;
}

const GRID_AXES: GridAxis[] = ['x', 'y', 'z'];
const MIN_CLOTH_GRID_STEP = 1 / 128;
const PIN_EDGE_REL = 1e-4;

function buildClothRest(geometry: BraceGeometry, settings: ClothSimSettings): RestMesh | null {
  if (!isFillGeometry(geometry)) return buildPathRest(geometry, settings);
  const source =
    settings.pin !== 'none' && settings.pin !== 'ends' && settings.gather > 1e-9
      ? expandFillForGather(geometry, settings.pin, settings.gather)
      : geometry;
  return settings.shape === 'sheet'
    ? buildSheetRest(source, settings.resolution, geometry)
    : buildCushionRest(source, settings.resolution, geometry);
}

/** 寄せで端が縮む分、先にピン方向へ布を長くし、寄せ後に元の大きさへ戻せるようにする。 */
function expandFillForGather(geometry: BraceGeometry, pin: ClothPin, gather: number): BraceGeometry {
  const plane = asPlaneGeometry(geometry);
  const points = tessellatePlanePolygon(plane);
  if (points.length < 3) return geometry;
  const raw = plane.normal ?? computePolygonNormal(points);
  if (!raw) return geometry;
  const normal = vec.normalize(raw);
  if (vec.length(normal) < 1e-9) return geometry;
  const along = pinAlong(normal, pin);
  const mid = spanMid(points, along);
  const scale = 1 / Math.max(1 - gather, 0.05);
  const nextPoints = expandPoints(points, along, mid, scale);
  const nextHoles = (plane.holes ?? []).map((ring) => expandPoints(ring, along, mid, scale));
  return {
    ...geometry,
    start: nextPoints[0],
    end: nextPoints[nextPoints.length - 1],
    points: nextPoints,
    closed: true,
    capHoles: nextHoles.length > 0 ? nextHoles : undefined,
    arcs: undefined,
  };
}

function pinAlong(normal: Vec3, pin: ClothPin): Vec3 {
  const { up, right } = clothFaceAxes(normal);
  return pin === 'top' || pin === 'bottom' ? right : up;
}

function spanMid(points: Vec3[], along: Vec3): number {
  let min = Infinity;
  let max = -Infinity;
  for (const p of points) {
    const t = p.x * along.x + p.y * along.y + p.z * along.z;
    if (t < min) min = t;
    if (t > max) max = t;
  }
  return (min + max) * 0.5;
}

function expandPoints(points: Vec3[], along: Vec3, mid: number, scale: number): Vec3[] {
  return points.map((p) => {
    const t = p.x * along.x + p.y * along.y + p.z * along.z;
    const d = mid + (t - mid) * scale - t;
    return { x: p.x + along.x * d, y: p.y + along.y * d, z: p.z + along.z * d };
  });
}

function buildCushionRest(
  geometry: BraceGeometry,
  resolution: number,
  stepFrom?: BraceGeometry
): RestMesh | null {
  if (geometry.fill !== true) return null;
  const tess = tessellateSlab(asPlaneGeometry(geometry));
  if (!tess) return null;
  const stepTess = stepFrom && stepFrom !== geometry ? tessellateSlab(asPlaneGeometry(stepFrom)) : tess;
  if (!stepTess) return null;
  let step = clothGridStep(stepTess, resolution);
  while (step <= 1 + 1e-12) {
    const mesh = buildSlabSurface(tess, step);
    if (mesh) return mesh;
    if (step >= 1) return null;
    step *= 2;
  }
  return null;
}

function clothGridStep(tess: TessellatedSlab, resolution: number): number {
  const points: Vec3[] = [];
  for (const p of tess.slab.start) {
    points.push(p);
    points.push(vec.add(p, tess.slab.offset));
  }
  return clothGridStepFromPoints(points, resolution);
}

function clothGridStepFromPoints(points: Vec3[], resolution: number): number {
  let minX = Infinity;
  let minY = Infinity;
  let minZ = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let maxZ = -Infinity;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.z < minZ) minZ = p.z;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
    if (p.z > maxZ) maxZ = p.z;
  }
  const longest = Math.max(maxX - minX, maxY - minY, maxZ - minZ);
  const target = longest / Math.max(1, resolution);
  let step = 1;
  while (step > target + 1e-12 && step > MIN_CLOTH_GRID_STEP) step /= 2;
  return step;
}

function buildSlabSurface(tess: TessellatedSlab, step: number): RestMesh | null {
  return buildFacesSurface(prismFaces(tess), step, tess.slab.normal);
}

function buildPathRest(geometry: BraceGeometry, settings: ClothSimSettings): RestMesh | null {
  const section = braceSectionRings(geometry);
  if (!section) return null;
  const prism = pathClothFaces(section.rings, section.closed, settings.shape, geometry);
  if (prism.length === 0) return null;
  const stepPoints = section.rings.flat();
  let step = clothGridStepFromPoints(stepPoints, settings.resolution);
  const normal = prism[0]?.normal ?? { x: 0, y: 1, z: 0 };
  while (step <= 1 + 1e-12) {
    const mesh = buildFacesSurface(prism, step, normal);
    if (mesh) {
      return {
        ...mesh,
        pathLine: { points: section.points, closed: section.closed },
      };
    }
    if (step >= 1) return null;
    step *= 2;
  }
  return null;
}

function pathClothFaces(
  rings: Vec3[][],
  closed: boolean,
  shape: ClothSimSettings['shape'],
  geometry: BraceGeometry
): PrismFace[] {
  const n = rings.length;
  if (n < 2) return [];
  const count = closed ? n : n - 1;
  const faces: PrismFace[] = [];
  const addQuad = (a: Vec3, b: Vec3, c: Vec3, d: Vec3) => {
    const normal = vec.normalize(vec.cross(vec.sub(b, a), vec.sub(d, a)));
    if (vec.length(normal) < 1e-9) return;
    faces.push({ points: [a, b, c, d], holes: [], normal });
  };
  const extruded = vec.length(braceExtrudeOffset(geometry)) >= 1e-9;
  if (shape === 'sheet' && extruded) {
    for (let i = 0; i < count; i++) {
      const a = rings[i];
      const b = rings[(i + 1) % n];
      if (a.length < 4 || b.length < 4) continue;
      addQuad(a[3], b[3], b[2], a[2]);
      addQuad(a[0], a[1], b[1], b[0]);
    }
    return faces;
  }
  for (let i = 0; i < count; i++) {
    const a = rings[i];
    const b = rings[(i + 1) % n];
    if (a.length < 4 || b.length < 4) continue;
    for (let k = 0; k < 4; k++) {
      const k1 = (k + 1) % 4;
      addQuad(a[k], a[k1], b[k1], b[k]);
    }
  }
  if (shape === 'volume' && !closed) {
    const start = rings[0];
    const end = rings[n - 1];
    if (start.length >= 4) addQuad(start[0], start[3], start[2], start[1]);
    if (end.length >= 4) addQuad(end[0], end[1], end[2], end[3]);
  }
  return faces;
}

function buildFacesSurface(prism: PrismFace[], step: number, normal: Vec3): RestMesh | null {
  if (prism.length === 0) return null;

  const positions: number[] = [];
  const shared = new Map<string, number>();
  const addWorld = (p: Vec3): number => {
    const id = `${Math.round(p.x * 1e5)}:${Math.round(p.y * 1e5)}:${Math.round(p.z * 1e5)}`;
    const existing = shared.get(id);
    if (existing !== undefined) return existing;
    const index = positions.length / 3;
    positions.push(p.x, p.y, p.z);
    shared.set(id, index);
    return index;
  };

  const faces: RestFace[] = [];
  const allIndices: number[] = [];
  for (const prismFace of prism) {
    const restFace = emptyFace();
    meshFaceOnGrid(prismFace, step, addWorld, restFace, allIndices);
    if (restFace.indices.length > 0) faces.push(restFace);
  }

  const splitVerts = faces.reduce((sum, face) => sum + face.uv.size, 0);
  if (
    faces.length === 0 ||
    positions.length / 3 > MAX_CLOTH_VERTICES ||
    splitVerts > MAX_CLOTH_VERTICES ||
    allIndices.length > MAX_CLOTH_INDICES
  ) {
    return null;
  }
  return { positions, indices: allIndices, faces, normal };
}

function buildSheetRest(
  geometry: BraceGeometry,
  resolution: number,
  stepFrom?: BraceGeometry
): RestMesh | null {
  if (geometry.fill !== true) return null;
  const plane = asPlaneGeometry(geometry);
  const points = tessellatePlanePolygon(plane);
  if (points.length < 3) return null;
  const raw = plane.normal ?? computePolygonNormal(points);
  if (!raw) return null;
  const normal = vec.normalize(raw);
  if (vec.length(normal) < 1e-9) return null;
  const origin = points[0];
  const holes = (plane.holes ?? []).map((ring) =>
    ring.map((p) => vec.sub(p, vec.scale(normal, vec.dot(vec.sub(p, origin), normal))))
  );
  const face: PrismFace = { points, holes, normal };
  const stepPoints = stepFrom ? tessellatePlanePolygon(asPlaneGeometry(stepFrom)) : points;
  let step = clothGridStepFromPoints(stepPoints.length >= 3 ? stepPoints : points, resolution);
  while (step <= 1 + 1e-12) {
    const mesh = buildSingleFace(face, step);
    if (mesh) return { ...mesh, normal };
    if (step >= 1) return null;
    step *= 2;
  }
  return null;
}

function buildSingleFace(face: PrismFace, step: number): Omit<RestMesh, 'normal'> | null {
  const positions: number[] = [];
  const shared = new Map<string, number>();
  const addWorld = (p: Vec3): number => {
    const id = `${Math.round(p.x * 1e5)}:${Math.round(p.y * 1e5)}:${Math.round(p.z * 1e5)}`;
    const existing = shared.get(id);
    if (existing !== undefined) return existing;
    const index = positions.length / 3;
    positions.push(p.x, p.y, p.z);
    shared.set(id, index);
    return index;
  };
  const restFace = emptyFace();
  const allIndices: number[] = [];
  meshFaceOnGrid(face, step, addWorld, restFace, allIndices);
  const faces = restFace.indices.length > 0 ? [restFace] : [];
  const splitVerts = faces.reduce((sum, item) => sum + item.uv.size, 0);
  if (
    faces.length === 0 ||
    positions.length / 3 > MAX_CLOTH_VERTICES ||
    splitVerts > MAX_CLOTH_VERTICES ||
    allIndices.length > MAX_CLOTH_INDICES
  ) {
    return null;
  }
  return { positions, indices: allIndices, faces };
}

interface PrismFace {
  points: Vec3[];
  holes: Vec3[][];
  normal: Vec3;
}

function prismFaces(tess: TessellatedSlab): PrismFace[] {
  const { slab, holeRings } = tess;
  const offset = slab.offset;
  const offsetLen = vec.length(offset);
  if (offsetLen < 1e-12) return [];
  const offsetDir = vec.scale(offset, 1 / offsetLen);
  const start = slab.start;
  const end = start.map((p) => vec.add(p, offset));
  const endHoles = holeRings.map((ring) => ring.map((p) => vec.add(p, offset)));
  const faces: PrismFace[] = [
    { points: start, holes: holeRings, normal: vec.scale(offsetDir, -1) },
    { points: end, holes: endHoles, normal: offsetDir },
  ];
  for (let i = 0; i < start.length; i++) {
    const a = start[i];
    const b = start[(i + 1) % start.length];
    const normal = vec.normalize(vec.cross(vec.sub(b, a), offset));
    if (vec.length(normal) < 1e-9) continue;
    faces.push({ points: [a, b, vec.add(b, offset), vec.add(a, offset)], holes: [], normal });
  }
  for (const ring of holeRings) {
    for (let i = 0; i < ring.length; i++) {
      const a = ring[i];
      const b = ring[(i + 1) % ring.length];
      const normal = vec.normalize(vec.cross(offset, vec.sub(b, a)));
      if (vec.length(normal) < 1e-9) continue;
      faces.push({ points: [a, b, vec.add(b, offset), vec.add(a, offset)], holes: [], normal });
    }
  }
  return faces;
}

function meshFaceOnGrid(
  face: PrismFace,
  step: number,
  addWorld: (p: Vec3) => number,
  restFace: RestFace,
  allIndices: number[]
): void {
  const normal = face.normal;
  if (vec.length(normal) < 1e-9 || face.points.length < 3) return;
  const origin = face.points[0];
  const { tangent, bitangent } = faceUvBasis(face.points, normal);
  const to2 = (p: Vec3): Point2D => {
    const d = vec.sub(p, origin);
    return { x: vec.dot(d, tangent), y: vec.dot(d, bitangent) };
  };
  const to3 = (p: Point2D): Vec3 =>
    vec.add(origin, vec.add(vec.scale(tangent, p.x), vec.scale(bitangent, p.y)));
  const span = faceSpanAxes(normal);
  const uvOf = (p: Vec3): [number, number] => {
    const g = gridCoords(p, 0);
    return [g[span[0]], g[span[1]]];
  };

  const outer = ensureWinding(face.points.map(to2), true);
  const holes = face.holes.map((ring) => ensureWinding(ring.map(to2), false));
  const levelsU = axisLevels(outer.map((p) => p.x), step);
  const levelsV = axisLevels(outer.map((p) => p.y), step);

  const emitPiece = (piece: Point2D[], pieceHoles: Point2D[][]) => {
    const tess =
      pieceHoles.length > 0
        ? triangulatePolygonWithHoles(piece, pieceHoles)
        : { vertices: piece, indices: triangulatePolygon(piece) };
    for (let t = 0; t < tess.indices.length; t += 3) {
      const ia = tess.indices[t];
      const ib = tess.indices[t + 1];
      const ic = tess.indices[t + 2];
      const pa = to3(tess.vertices[ia]);
      const pb = to3(tess.vertices[ib]);
      const pc = to3(tess.vertices[ic]);
      const a = addWorld(pa);
      const b = addWorld(pb);
      const c = addWorld(pc);
      if (a === b || b === c || c === a) continue;
      restFace.indices.push(a, b, c);
      restFace.uv.set(a, uvOf(pa));
      restFace.uv.set(b, uvOf(pb));
      restFace.uv.set(c, uvOf(pc));
      allIndices.push(a, b, c);
    }
  };

  for (let i = 0; i < levelsU.length - 1; i++) {
    for (let j = 0; j < levelsV.length - 1; j++) {
      const clip = ensureWinding(
        [
          { x: levelsU[i], y: levelsV[j] },
          { x: levelsU[i + 1], y: levelsV[j] },
          { x: levelsU[i + 1], y: levelsV[j + 1] },
          { x: levelsU[i], y: levelsV[j + 1] },
        ],
        true
      );
      const piece = convexIntersect(outer, clip);
      if (piece.length < 3) continue;
      const clippedHoles = holes
        .map((hole) => convexIntersect(hole, clip))
        .filter((hole) => hole.length >= 3);
      emitPiece(piece, clippedHoles);
    }
  }
}

function faceSpanAxes(normal: Vec3): [GridAxis, GridAxis] {
  const align: Record<GridAxis, number> = {
    x: Math.abs(normal.x),
    y: Math.abs(normal.y),
    z: Math.abs(normal.z),
  };
  const thin = GRID_AXES.reduce((best, axis) => (align[axis] > align[best] ? axis : best));
  const span = GRID_AXES.filter((axis) => axis !== thin) as [GridAxis, GridAxis];
  return span;
}

/** 最長辺を U、面内の直交方向を V。世界軸だと傾いた矩形のセルが菱形になる。 */
function faceUvBasis(points: Vec3[], normal: Vec3): { tangent: Vec3; bitangent: Vec3 } {
  let bestLen = 0;
  let tangent = { x: 0, y: 0, z: 0 };
  for (let i = 0; i < points.length; i++) {
    const edge = vec.sub(points[(i + 1) % points.length], points[i]);
    const onPlane = vec.sub(edge, vec.scale(normal, vec.dot(edge, normal)));
    const len = vec.length(onPlane);
    if (len > bestLen) {
      bestLen = len;
      tangent = vec.scale(onPlane, 1 / len);
    }
  }
  if (bestLen < 1e-9) return planeBasis(normal);
  const bitangent = vec.normalize(vec.cross(normal, tangent));
  if (vec.length(bitangent) < 1e-9) return planeBasis(normal);
  return { tangent, bitangent };
}

function axisLevels(values: number[], step: number): number[] {
  let min = Infinity;
  let max = -Infinity;
  for (const value of values) {
    if (value < min) min = value;
    if (value > max) max = value;
  }
  const levels = new Set<number>([min, max, ...gridLevels(min, max, step)]);
  return [...levels].sort((a, b) => a - b);
}

function emptyFace(): RestFace {
  return { indices: [], uv: new Map() };
}

function convexIntersect(a: Point2D[], b: Point2D[]): Point2D[] {
  const points: Point2D[] = [];
  const push = (point: Point2D) => {
    if (points.some((existing) => Math.hypot(existing.x - point.x, existing.y - point.y) < 1e-6)) return;
    points.push(point);
  };
  for (const p of a) if (pointInPolygon(p, b, true)) push(p);
  for (const p of b) if (pointInPolygon(p, a, true)) push(p);
  for (let i = 0; i < a.length; i++) {
    const a0 = a[i];
    const a1 = a[(i + 1) % a.length];
    for (let j = 0; j < b.length; j++) {
      const hit = segmentHit(a0, a1, b[j], b[(j + 1) % b.length]);
      if (hit) push(hit);
    }
  }
  if (points.length < 3) return [];
  const cx = points.reduce((s, p) => s + p.x, 0) / points.length;
  const cy = points.reduce((s, p) => s + p.y, 0) / points.length;
  points.sort((p, q) => Math.atan2(p.y - cy, p.x - cx) - Math.atan2(q.y - cy, q.x - cx));
  return points;
}

function segmentHit(a: Point2D, b: Point2D, c: Point2D, d: Point2D): Point2D | null {
  const rx = b.x - a.x;
  const ry = b.y - a.y;
  const sx = d.x - c.x;
  const sy = d.y - c.y;
  const den = rx * sy - ry * sx;
  if (Math.abs(den) < 1e-9) return null;
  const t = ((c.x - a.x) * sy - (c.y - a.y) * sx) / den;
  const u = ((c.x - a.x) * ry - (c.y - a.y) * rx) / den;
  if (t < -1e-9 || t > 1 + 1e-9 || u < -1e-9 || u > 1 + 1e-9) return null;
  return { x: a.x + rx * t, y: a.y + ry * t };
}

function createSimState(rest: RestMesh, settings: ClothSimSettings): SimState {
  const count = rest.positions.length / 3;
  const x = new Float64Array(count);
  const y = new Float64Array(count);
  const z = new Float64Array(count);
  let floorY = Infinity;
  for (let i = 0; i < count; i++) {
    x[i] = rest.positions[i * 3];
    y[i] = rest.positions[i * 3 + 1];
    z[i] = rest.positions[i * 3 + 2];
    if (y[i] < floorY) floorY = y[i];
  }
  const constraints = buildDistanceConstraints(rest);
  const pinned = markPinned(x, y, z, rest, settings.pin);
  if (settings.pin !== 'none' && settings.pin !== 'ends' && settings.gather > 1e-9) {
    gatherPinned(x, y, z, pinned, rest.normal, settings.pin, settings.gather);
    seedGatherFolds(x, y, z, pinned, rest.normal, settings.pin, settings.gather);
  }
  const pinX = new Float64Array(x);
  const pinY = new Float64Array(y);
  const pinZ = new Float64Array(z);
  const stiffness = settings.stiffness;
  const compliance = ((1 - stiffness) * (1 - stiffness)) / (stiffness * stiffness + 1e-6) * 1e-6;
  return {
    x,
    y,
    z,
    px: new Float64Array(x),
    py: new Float64Array(y),
    pz: new Float64Array(z),
    pinX,
    pinY,
    pinZ,
    pinned,
    constraints,
    indices: rest.indices,
    faces: rest.faces,
    inflate: settings.shape === 'sheet' ? 0 : settings.inflate,
    gravity: settings.gravity,
    compliance,
    floorY,
    useFloor: settings.pin === 'none',
  };
}

function clothFaceAxes(normal: Vec3): { up: Vec3; right: Vec3 } {
  const n = vec.normalize(normal);
  const worldUp = { x: 0, y: 1, z: 0 };
  let up = vec.sub(worldUp, vec.scale(n, vec.dot(worldUp, n)));
  if (vec.length(up) < 1e-6) {
    const worldNorth = { x: 0, y: 0, z: 1 };
    up = vec.sub(worldNorth, vec.scale(n, vec.dot(worldNorth, n)));
    if (vec.length(up) < 1e-6) up = { x: 0, y: 0, z: 1 };
  }
  up = vec.normalize(up);
  let right = vec.cross(up, n);
  if (vec.length(right) < 1e-6) right = { x: 1, y: 0, z: 0 };
  right = vec.normalize(right);
  return { up, right };
}

function pinAxis(normal: Vec3, pin: ClothPin): { axis: Vec3; extreme: 'min' | 'max' } | null {
  if (pin === 'none' || pin === 'ends') return null;
  const { up, right } = clothFaceAxes(normal);
  if (pin === 'top') return { axis: up, extreme: 'max' };
  if (pin === 'bottom') return { axis: up, extreme: 'min' };
  if (pin === 'right') return { axis: right, extreme: 'max' };
  return { axis: right, extreme: 'min' };
}

function markPinned(
  x: Float64Array,
  y: Float64Array,
  z: Float64Array,
  rest: RestMesh,
  pin: ClothPin
): Uint8Array {
  if (pin === 'ends') {
    if (rest.pathLine && !rest.pathLine.closed) {
      return markPathEnds(x, y, z, rest.pathLine.points);
    }
    return markBothFaceEnds(x, y, z, rest.normal);
  }
  const pinned = new Uint8Array(x.length);
  const spec = pinAxis(rest.normal, pin);
  if (!spec) return pinned;
  let min = Infinity;
  let max = -Infinity;
  const coords = new Float64Array(x.length);
  for (let i = 0; i < x.length; i++) {
    const c = x[i] * spec.axis.x + y[i] * spec.axis.y + z[i] * spec.axis.z;
    coords[i] = c;
    if (c < min) min = c;
    if (c > max) max = c;
  }
  const eps = Math.max((max - min) * PIN_EDGE_REL, 1e-6);
  const limit = spec.extreme === 'max' ? max - eps : min + eps;
  for (let i = 0; i < x.length; i++) {
    pinned[i] = spec.extreme === 'max' ? (coords[i] >= limit ? 1 : 0) : coords[i] <= limit ? 1 : 0;
  }
  return pinned;
}

function markBothFaceEnds(
  x: Float64Array,
  y: Float64Array,
  z: Float64Array,
  normal: Vec3
): Uint8Array {
  const left = markPinned(x, y, z, { positions: [], indices: [], faces: [], normal }, 'left');
  const right = markPinned(x, y, z, { positions: [], indices: [], faces: [], normal }, 'right');
  const pinned = new Uint8Array(x.length);
  for (let i = 0; i < x.length; i++) pinned[i] = left[i] || right[i] ? 1 : 0;
  return pinned;
}

function markPathEnds(x: Float64Array, y: Float64Array, z: Float64Array, points: Vec3[]): Uint8Array {
  const pinned = new Uint8Array(x.length);
  const total = polylineLength(points);
  if (total < 1e-9) return pinned;
  const endWidth = Math.max(total * PIN_EDGE_REL, 1e-6);
  for (let i = 0; i < x.length; i++) {
    const s = closestPolylineS({ x: x[i], y: y[i], z: z[i] }, points);
    if (s <= endWidth || s >= total - endWidth) pinned[i] = 1;
  }
  return pinned;
}

function polylineLength(points: Vec3[]): number {
  let total = 0;
  for (let i = 0; i < points.length - 1; i++) {
    total += vec.length(vec.sub(points[i + 1], points[i]));
  }
  return total;
}

function closestPolylineS(p: Vec3, points: Vec3[]): number {
  let best = 0;
  let bestDist = Infinity;
  let acc = 0;
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i];
    const b = points[i + 1];
    const ab = vec.sub(b, a);
    const len = vec.length(ab);
    if (len < 1e-12) continue;
    const t = Math.min(1, Math.max(0, vec.dot(vec.sub(p, a), ab) / (len * len)));
    const q = vec.add(a, vec.scale(ab, t));
    const d = vec.length(vec.sub(p, q));
    if (d < bestDist) {
      bestDist = d;
      best = acc + t * len;
    }
    acc += len;
  }
  return best;
}

function gatherPinned(
  x: Float64Array,
  y: Float64Array,
  z: Float64Array,
  pinned: Uint8Array,
  normal: Vec3,
  pin: ClothPin,
  gather: number
): void {
  const { up, right } = clothFaceAxes(normal);
  const along = pin === 'top' || pin === 'bottom' ? right : up;
  let tMin = Infinity;
  let tMax = -Infinity;
  for (let i = 0; i < x.length; i++) {
    if (!pinned[i]) continue;
    const t = x[i] * along.x + y[i] * along.y + z[i] * along.z;
    if (t < tMin) tMin = t;
    if (t > tMax) tMax = t;
  }
  if (!(tMax > tMin)) return;
  const mid = (tMin + tMax) * 0.5;
  const scale = 1 - gather;
  for (let i = 0; i < x.length; i++) {
    if (!pinned[i]) continue;
    const t = x[i] * along.x + y[i] * along.y + z[i] * along.z;
    const next = mid + (t - mid) * scale;
    const d = next - t;
    x[i] += along.x * d;
    y[i] += along.y * d;
    z[i] += along.z * d;
  }
}

/**
 * 軸平行な一枚だと寄せ余長が面内に潰れる。
 * 法線方向へ正弦の種を入れて座屈させ、ヒダにする。
 */
function seedGatherFolds(
  x: Float64Array,
  y: Float64Array,
  z: Float64Array,
  pinned: Uint8Array,
  normal: Vec3,
  pin: ClothPin,
  gather: number
): void {
  const n = vec.normalize(normal);
  if (vec.length(n) < 1e-9) return;
  const { up, right } = clothFaceAxes(n);
  const along = pin === 'top' || pin === 'bottom' ? right : up;
  const across = pin === 'top' || pin === 'bottom' ? up : right;
  let aMin = Infinity;
  let aMax = -Infinity;
  let cMin = Infinity;
  let cMax = -Infinity;
  for (let i = 0; i < x.length; i++) {
    const a = x[i] * along.x + y[i] * along.y + z[i] * along.z;
    const c = x[i] * across.x + y[i] * across.y + z[i] * across.z;
    if (a < aMin) aMin = a;
    if (a > aMax) aMax = a;
    if (c < cMin) cMin = c;
    if (c > cMax) cMax = c;
  }
  const width = aMax - aMin;
  const height = cMax - cMin;
  if (width < 1e-9 || height < 1e-9) return;
  const waves = Math.max(3, Math.round(3 + gather * 4));
  const amp = width * gather * 0.18;
  const pinIsMax = pin === 'top' || pin === 'right';
  for (let i = 0; i < x.length; i++) {
    if (pinned[i]) continue;
    const a = (x[i] * along.x + y[i] * along.y + z[i] * along.z - aMin) / width;
    const c = (x[i] * across.x + y[i] * across.y + z[i] * across.z - cMin) / height;
    const fromPin = pinIsMax ? c : 1 - c;
    const offset = amp * Math.sin(a * waves * Math.PI) * Math.sin(Math.PI * fromPin);
    x[i] += n.x * offset;
    y[i] += n.y * offset;
    z[i] += n.z * offset;
  }
}

function buildDistanceConstraints(rest: RestMesh): DistConstraint[] {
  const seen = new Set<string>();
  const constraints: DistConstraint[] = [];
  const add = (a: number, b: number) => {
    if (a === b) return;
    const lo = a < b ? a : b;
    const hi = a < b ? b : a;
    const id = `${lo}:${hi}`;
    if (seen.has(id)) return;
    seen.add(id);
    const ax = rest.positions[a * 3];
    const ay = rest.positions[a * 3 + 1];
    const az = rest.positions[a * 3 + 2];
    const bx = rest.positions[b * 3];
    const by = rest.positions[b * 3 + 1];
    const bz = rest.positions[b * 3 + 2];
    const len = Math.hypot(bx - ax, by - ay, bz - az);
    if (len < 1e-9) return;
    constraints.push({ i: a, j: b, rest: len });
  };
  for (let t = 0; t < rest.indices.length; t += 3) {
    const a = rest.indices[t];
    const b = rest.indices[t + 1];
    const c = rest.indices[t + 2];
    add(a, b);
    add(b, c);
    add(c, a);
  }
  // 曲げ: 各頂点の 1 つ飛ばしは三角形辺だけでは足りないので、共有辺の対角を足す
  const opposite = new Map<string, number>();
  for (let t = 0; t < rest.indices.length; t += 3) {
    const tri = [rest.indices[t], rest.indices[t + 1], rest.indices[t + 2]];
    for (let e = 0; e < 3; e++) {
      const a = tri[e];
      const b = tri[(e + 1) % 3];
      const c = tri[(e + 2) % 3];
      const lo = a < b ? a : b;
      const hi = a < b ? b : a;
      const id = `${lo}:${hi}`;
      const other = opposite.get(id);
      if (other === undefined) opposite.set(id, c);
      else add(other, c);
    }
  }
  return constraints;
}

function stepSim(state: SimState): void {
  const { x, y, z, px, py, pz, pinned } = state;
  const n = x.length;
  const gy = -state.gravity * 9.8 * DT * DT;
  for (let i = 0; i < n; i++) {
    if (pinned[i]) continue;
    const vx = (x[i] - px[i]) * DAMPING;
    const vy = (y[i] - py[i]) * DAMPING;
    const vz = (z[i] - pz[i]) * DAMPING;
    px[i] = x[i];
    py[i] = y[i];
    pz[i] = z[i];
    x[i] += vx;
    y[i] += vy + gy;
    z[i] += vz;
  }
  applyPressure(state);
  holdPinned(state);
  const alpha = state.compliance / (DT * DT);
  for (let iter = 0; iter < XPBD_ITERS; iter++) {
    for (const c of state.constraints) {
      const i = c.i;
      const j = c.j;
      const wi = pinned[i] ? 0 : 1;
      const wj = pinned[j] ? 0 : 1;
      if (wi + wj === 0) continue;
      const dx = x[j] - x[i];
      const dy = y[j] - y[i];
      const dz = z[j] - z[i];
      const len = Math.hypot(dx, dy, dz);
      if (len < 1e-12) continue;
      const dlambda = -(len - c.rest) / (wi + wj + alpha);
      const s = dlambda / len;
      x[i] -= dx * s * wi;
      y[i] -= dy * s * wi;
      z[i] -= dz * s * wi;
      x[j] += dx * s * wj;
      y[j] += dy * s * wj;
      z[j] += dz * s * wj;
    }
  }
  holdPinned(state);
  if (!state.useFloor) return;
  for (let i = 0; i < n; i++) {
    if (y[i] < state.floorY) y[i] = state.floorY;
  }
}

function holdPinned(state: SimState): void {
  const { x, y, z, pinX, pinY, pinZ, pinned } = state;
  for (let i = 0; i < x.length; i++) {
    if (!pinned[i]) continue;
    x[i] = pinX[i];
    y[i] = pinY[i];
    z[i] = pinZ[i];
  }
}

function applyPressure(state: SimState): void {
  if (state.inflate <= 1e-9) return;
  const { x, y, z, indices } = state;
  const scale = state.inflate * PRESSURE_SCALE;
  const accX = new Float64Array(x.length);
  const accY = new Float64Array(x.length);
  const accZ = new Float64Array(x.length);
  for (let t = 0; t < indices.length; t += 3) {
    const ia = indices[t];
    const ib = indices[t + 1];
    const ic = indices[t + 2];
    const abx = x[ib] - x[ia];
    const aby = y[ib] - y[ia];
    const abz = z[ib] - z[ia];
    const acx = x[ic] - x[ia];
    const acy = y[ic] - y[ia];
    const acz = z[ic] - z[ia];
    const nx = (aby * acz - abz * acy) * scale;
    const ny = (abz * acx - abx * acz) * scale;
    const nz = (abx * acy - aby * acx) * scale;
    const w = 1 / 3;
    accX[ia] += nx * w;
    accY[ia] += ny * w;
    accZ[ia] += nz * w;
    accX[ib] += nx * w;
    accY[ib] += ny * w;
    accZ[ib] += nz * w;
    accX[ic] += nx * w;
    accY[ic] += ny * w;
    accZ[ic] += nz * w;
  }
  for (let i = 0; i < x.length; i++) {
    if (state.pinned[i]) continue;
    x[i] += accX[i];
    y[i] += accY[i];
    z[i] += accZ[i];
  }
}

function bakeState(state: SimState, settings: ClothSimSettings): ClothBake {
  const mesh = expandFaceUvs(state);
  const normals = computeNormals(mesh.positions, mesh.indices);
  return {
    settings: { ...settings },
    mesh: {
      positions: mesh.positions,
      normals,
      uvs: mesh.uvs,
      indices: mesh.indices,
    },
  };
}

/** 面ごとに頂点を分け、その面で動く2軸を UV にする。共有頂点のままだと側面が1軸つぶれる。 */
function expandFaceUvs(state: SimState): { positions: number[]; uvs: number[]; indices: number[] } {
  const { x, y, z, faces } = state;
  const positions: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  for (let f = 0; f < faces.length; f++) {
    const face = faces[f];
    const remap = new Map<number, number>();
    const emit = (src: number): number => {
      const existing = remap.get(src);
      if (existing !== undefined) return existing;
      const uv = face.uv.get(src) ?? [0, 0];
      const index = positions.length / 3;
      positions.push(round4(x[src]), round4(y[src]), round4(z[src]));
      uvs.push(round4(uv[0]), round4(uv[1]));
      remap.set(src, index);
      return index;
    };
    for (const src of face.indices) indices.push(emit(src));
  }
  return { positions, uvs, indices };
}

function computeNormals(positions: number[], indices: number[]): number[] {
  const normals = new Array<number>(positions.length).fill(0);
  for (let t = 0; t < indices.length; t += 3) {
    const ia = indices[t] * 3;
    const ib = indices[t + 1] * 3;
    const ic = indices[t + 2] * 3;
    const abx = positions[ib] - positions[ia];
    const aby = positions[ib + 1] - positions[ia + 1];
    const abz = positions[ib + 2] - positions[ia + 2];
    const acx = positions[ic] - positions[ia];
    const acy = positions[ic + 1] - positions[ia + 1];
    const acz = positions[ic + 2] - positions[ia + 2];
    const nx = aby * acz - abz * acy;
    const ny = abz * acx - abx * acz;
    const nz = abx * acy - aby * acx;
    normals[ia] += nx;
    normals[ia + 1] += ny;
    normals[ia + 2] += nz;
    normals[ib] += nx;
    normals[ib + 1] += ny;
    normals[ib + 2] += nz;
    normals[ic] += nx;
    normals[ic + 1] += ny;
    normals[ic + 2] += nz;
  }
  for (let i = 0; i < normals.length; i += 3) {
    const len = Math.hypot(normals[i], normals[i + 1], normals[i + 2]);
    if (len < 1e-9) {
      normals[i] = 0;
      normals[i + 1] = 1;
      normals[i + 2] = 0;
    } else {
      normals[i] = round4(normals[i] / len);
      normals[i + 1] = round4(normals[i + 1] / len);
      normals[i + 2] = round4(normals[i + 2] / len);
    }
  }
  return normals;
}

function yieldFrame(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => resolve());
    } else {
      setTimeout(resolve, 0);
    }
  });
}

function pickEnum<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value) ? (value as T) : fallback;
}

function clampInt(value: unknown, fallback: number, min: number, max: number): number {
  const n = typeof value === 'number' && Number.isFinite(value) ? Math.round(value) : fallback;
  return Math.min(max, Math.max(min, n));
}

function clampNum(value: unknown, fallback: number, min: number, max: number): number {
  const n = typeof value === 'number' && Number.isFinite(value) ? value : fallback;
  return Math.min(max, Math.max(min, n));
}

function round4(value: number): number {
  return Math.round(value * 10000) / 10000;
}
